import { ref, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useWavoipStore } from 'dashboard/stores/wavoip';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

// [FIX 2026-02-18] Call-state polling interval — MODULE SCOPE singleton.
// Must be outside useWavoip() because multiple components (WavoipInitializer,
// WavoipDialerButton) call the composable independently. If this were inside
// the function, each caller would get its own interval reference, leading to
// duplicate polling and impossible-to-clear orphaned intervals.
let callStateInterval = null;

/**
 * Composable for managing the WaVoIP webphone lifecycle.
 * Initializes the webphone for all accounts,
 * syncs dark/light theme, manages device tokens from inboxes,
 * and handles incoming/outgoing calls.
 */
export function useWavoip() {
  const store = useStore();
  const wavoipStore = useWavoipStore();
  const currentAccountId = computed(() => store.getters.getCurrentAccountId);
  const isFeatureEnabled = useMapGetter('accounts/isFeatureEnabledonAccount');

  const isLoading = ref(false);
  const error = ref(null);

  // WaVoIP requires BOTH:
  // 1. The wavoip_calls feature flag enabled on the account (SuperAdmin toggle)
  // 2. At least one inbox with a wavoip_token configured
  const isWavoipEnabled = computed(() => {
    // Check feature flag first (cheap)
    if (
      !isFeatureEnabled.value ||
      !isFeatureEnabled.value(
        currentAccountId.value,
        FEATURE_FLAGS.WAVOIP_CALLS
      )
    ) {
      return false;
    }

    // Then check if any inbox has a token (heavier)
    const inboxes = store.getters['inboxes/getInboxes'] || [];
    return inboxes.some(inbox => {
      if (inbox.channel_type !== 'Channel::Api') return false;
      const token =
        inbox.additional_attributes?.wavoip_token ||
        inbox.channel?.additional_attributes?.wavoip_token;
      return !!token;
    });
  });

  // Get all WaVoIP tokens from WhatsApp Web inboxes
  const getInboxWavoipTokens = () => {
    const inboxes = store.getters['inboxes/getInboxes'] || [];
    const tokens = [];

    inboxes.forEach(inbox => {
      // Only API channel inboxes (WhatsApp Web) with wavoip_token
      if (inbox.channel_type === 'Channel::Api') {
        const token =
          inbox.additional_attributes?.wavoip_token ||
          inbox.channel?.additional_attributes?.wavoip_token;
        if (token) {
          tokens.push({
            token,
            inboxId: inbox.id,
            inboxName: inbox.name,
          });
        }
      }
    });

    return tokens;
  };

  // [FIX 2026-02-18] Poll the SDK for call state changes since it lacks onEnd events.
  // Checks getCallActive() and getCallOutgoing() every 1.5s.
  // When both return undefined → call ended → clear store state → button re-enables.
  const stopCallStatePolling = () => {
    if (callStateInterval) {
      clearInterval(callStateInterval);
      callStateInterval = null;
    }
  };

  const startCallStatePolling = api => {
    // Don't start multiple intervals
    if (callStateInterval) return;

    callStateInterval = setInterval(() => {
      try {
        const activeCall = api.call.getCallActive();
        const outgoingCall = api.call.getCallOutgoing();

        if (activeCall) {
          // A call is active — update store if not already set
          if (!wavoipStore.isCallActive) {
            wavoipStore.setActiveCall({
              id: activeCall.id,
              status: activeCall.status,
              peer: activeCall.peer,
            });
          }
        } else if (outgoingCall) {
          // Still outgoing/ringing — keep the outgoing state
          if (!wavoipStore.isCallOutgoing) {
            wavoipStore.setCallOutgoing(true);
          }
        } else {
          // No active or outgoing call — call has ended
          if (wavoipStore.isCallActive || wavoipStore.isCallOutgoing) {
            // eslint-disable-next-line no-console
            console.log('[WaVoIP] Call ended — clearing state');
            wavoipStore.clearActiveCall();
            wavoipStore.clearIncomingOffer();
          }
          // Stop polling when idle (will restart on next offer or makeCall)
          stopCallStatePolling();
        }
      } catch {
        // SDK may throw if destroyed mid-poll — just stop
        stopCallStatePolling();
      }
    }, 1500);
  };

  // Initialize the webphone widget
  const initWebphone = async () => {
    if (!isWavoipEnabled.value || wavoipStore.isInitialized) return;

    // Double-check: only proceed if we have actual tokens to register
    const inboxTokens = getInboxWavoipTokens();
    if (inboxTokens.length === 0) return;

    isLoading.value = true;
    error.value = null;

    try {
      const WavoipWebphone = (await import('@wavoip/wavoip-webphone')).default;

      // Detect current theme
      const isDarkMode = document.documentElement.classList.contains('dark');

      const api = await WavoipWebphone.render({
        theme: isDarkMode ? 'dark' : 'light',
        widget: {
          showWidgetButton: false, // We control the button ourselves
          startOpen: false,
        },
        position: 'bottom-right',
        statusBar: {
          showNotificationsIcon: false,
          showSettingsIcon: false,
        },
        settingsMenu: {
          deviceMenu: {
            show: false,
            showAddDevices: false,
            showEnableDevicesButton: false,
            showRemoveDevicesButton: false,
          },
        },
      });

      wavoipStore.setWebphoneAPI(api);

      // Add device tokens from inboxes (tokens already fetched above)
      inboxTokens.forEach(({ token }) => {
        api.device.add(token, true);
        wavoipStore.addDeviceToken(token);
      });

      // Listen for incoming calls
      api.call.onOffer(offer => {
        wavoipStore.setIncomingOffer({
          id: offer.id,
          phone: offer.peer?.phone || '',
          displayName: offer.peer?.displayName || '',
          profilePicture: offer.peer?.profilePicture || null,
          deviceToken: offer.device_token,
          direction: offer.direction,
        });
        // Start polling call state when an offer arrives
        startCallStatePolling(api);
      });

      // [FIX 2026-02-18] Start polling immediately in case a call is already active
      // (e.g., page was reloaded during a call)
      startCallStatePolling(api);

      // eslint-disable-next-line no-console
      console.log(
        '[WaVoIP] Webphone initialized with',
        inboxTokens.length,
        'device(s)'
      );
    } catch (err) {
      error.value = err.message || 'Failed to initialize WaVoIP webphone';
      // eslint-disable-next-line no-console
      console.error('[WaVoIP] Init error:', err);
    } finally {
      isLoading.value = false;
    }
  };

  // Destroy the webphone widget
  const destroyWebphone = async () => {
    if (!wavoipStore.isInitialized) return;

    // [FIX 2026-02-18] Clean up call-state polling
    stopCallStatePolling();

    try {
      const WavoipWebphone = (await import('@wavoip/wavoip-webphone')).default;
      WavoipWebphone.destroy();
    } catch {
      // Ignore destroy errors
    }

    wavoipStore.reset();
  };

  // Make an outgoing call
  const makeCall = async (phoneNumber, fromToken = null) => {
    if (!wavoipStore.isInitialized) {
      error.value = 'WaVoIP not initialized';
      return;
    }

    await wavoipStore.makeCall(phoneNumber, fromToken);

    // [FIX 2026-02-18] Start polling to detect when the outgoing call ends
    if (wavoipStore.webphoneAPI) {
      startCallStatePolling(wavoipStore.webphoneAPI);
    }
  };

  // Open/close the webphone widget
  const toggleWebphone = () => {
    if (wavoipStore.webphoneAPI) {
      wavoipStore.webphoneAPI.widget.toggle();
    }
  };

  const openWebphone = () => {
    if (wavoipStore.webphoneAPI) {
      wavoipStore.webphoneAPI.widget.open();
    }
  };

  const closeWebphone = () => {
    if (wavoipStore.webphoneAPI) {
      wavoipStore.webphoneAPI.widget.close();
    }
  };

  // Set the dialer input phone number
  const setDialerInput = phoneNumber => {
    if (wavoipStore.webphoneAPI) {
      wavoipStore.webphoneAPI.call.setInput(phoneNumber);
    }
  };

  return {
    // State
    isLoading,
    error,
    isWavoipEnabled,

    // Store refs
    wavoipStore,

    // Actions
    initWebphone,
    destroyWebphone,
    makeCall,
    toggleWebphone,
    openWebphone,
    closeWebphone,
    setDialerInput,
    getInboxWavoipTokens,
  };
}
